import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { useAsyncFn } from 'react-use';

import { LbInput } from '@infoods/lifebase-ui';

import classes from './CreateScanIqSubscriptionModal.module.scss';

import { CreateEntitySubscriptionInfo } from '../../../interfaces/entitySubscription';
import { LbBigModal } from '../../../../shared/components/modals';
import { HiddenInput } from '../../../../shared/components/form-components';
import { CreateSubscriptionFormValues } from '../../../interfaces/createSubscription';
import { SubscriptionOptionBox } from '../components/SubscriptionOptionBox/SubscriptionOptionBox';
import { BILLING_PERIOD_TYPE } from '../../../constants/billingPeriodType';
import { SUBSCRIPTION } from '../../../../shared/constants/user-info/subscription';
import { getSubscriptionInfoFromFormValue } from '../utils/getSubscriptionInfoFromFormValue';
import { PricingForm } from './containers/PricingForm/PricingForm';
import { usePaymentDetails } from '../hooks/usePaymentDetails';
import { useFormChange } from '../../../../shared/hooks/useFormChange';
import { defaultFormValue } from './CreateScanIqSubscriptionModal.settings';

type CreateScanIqSubscriptionModalProps = {
  entityId: number;
  onClose: () => void;
  onSubmit: (data: CreateEntitySubscriptionInfo) => Promise<void>;
};
// defaultFormValue is nesessary because of resetField
const CreateScanIqSubscriptionModal = ({
  entityId,
  onClose,
  onSubmit,
}: CreateScanIqSubscriptionModalProps) => {
  const { control, register, handleSubmit, watch, resetField } =
    useForm<CreateSubscriptionFormValues>({
      defaultValues: defaultFormValue,
    });

  // Reset subscriptionPrice when type of subscription (billingPeriodType) changes
  const billingPeriodType = watch('billingPeriodType');

  useEffect(() => {
    resetField('subscriptionPrice');
  }, [billingPeriodType]);

  const handleFormSubmit = handleSubmit(async (data) => {
    const subscriptionInfo = getSubscriptionInfoFromFormValue(data);

    await onSubmit(subscriptionInfo);
    onClose();
  });

  const [state, createSubscription] = useAsyncFn(handleFormSubmit);

  const {
    monthlyRenewalPaymentDetail,
    getPaymentDetails,
    isLoading: isLoadingPaymentDetails,
  } = usePaymentDetails(entityId);

  const handleGetPaymentDetails = async (data: CreateSubscriptionFormValues) => {
    const { name, ...paymentInfo } = getSubscriptionInfoFromFormValue(data);

    await getPaymentDetails(paymentInfo);
  };

  useFormChange({
    watch,
    onChange: handleGetPaymentDetails,
    ignoreFields: ['name'],
  });

  return (
    <LbBigModal
      open
      title="Create Subscription"
      buttonOptions={{
        yesText: 'Save',
        noText: 'Cancel',
        noHandler: onClose,
        yesHandler: createSubscription,
      }}
      isLoading={state.loading}
    >
      <form className={classes.modalContent}>
        <HiddenInput
          register={register}
          name="subscriptionType"
          value={SUBSCRIPTION.SCAN_IQ}
        />
        <LbInput
          control={control}
          name="name"
          label="Name"
          variant="outlined"
          size="small"
          rules={{ required: 'Name is required' }}
        />
        <SubscriptionOptionBox
          control={control}
          optionValue={BILLING_PERIOD_TYPE.DAILY}
          name="billingPeriodType"
          title="One-Time Payment per scan"
          description="A single, non-recurring fee charged for each individual scan performed"
        >
          <PricingForm
            control={control}
            title="One-Time Payment"
            description="Covers a single InBody scan and body composition analysis. Each scan requires separate payment."
            paymentDetails={monthlyRenewalPaymentDetail}
            isLoadingPaymentDetails={isLoadingPaymentDetails}
          />
        </SubscriptionOptionBox>
        <SubscriptionOptionBox
          control={control}
          optionValue={BILLING_PERIOD_TYPE.MONTHLY}
          name="billingPeriodType"
          title="Monthly"
          description="Recurring subscription for customers to use ScanIQ for one month"
        >
          <PricingForm
            control={control}
            title="Monthly Renewal"
            description="This will be a recurring monthly payment with no initial plan"
            paymentDetails={monthlyRenewalPaymentDetail}
            isLoadingPaymentDetails={isLoadingPaymentDetails}
          />
        </SubscriptionOptionBox>
      </form>
    </LbBigModal>
  );
};

export { CreateScanIqSubscriptionModal };
